/**
 * Human-readable player identity used across every planner projection.
 *
 * The board and raid projections carry all three Discord name layers. The
 * legacy `discordName` alias remains a fallback for old snapshots while the
 * explicit fields let the planner switch presentation without refetching.
 */
import { PlayerNameMode } from '../types'
import type { PlayerNameMode as PlayerNameModeType } from '../types'

export interface PlayerIdentity {
  readonly discordName: string
  readonly guildNickname: string
  readonly playerNickname: string
  readonly playerAccountName: string
  readonly inGameName: string
}

function withoutMentionPrefix(value: string): string {
  return value.trim().replace(/^@+/, '')
}

function firstName(values: ReadonlyArray<string>): string {
  for (const value of values) {
    const normalized = withoutMentionPrefix(value)
    if (normalized !== '') return normalized
  }
  return ''
}

export function playerNameForMode(
  identity: PlayerIdentity,
  mode: PlayerNameModeType = PlayerNameMode.GuildNickname,
): string {
  switch (mode) {
    case PlayerNameMode.PlayerNickname:
      return firstName([
        identity.playerNickname,
        identity.playerAccountName,
        identity.guildNickname,
        identity.discordName,
      ])
    case PlayerNameMode.PlayerAccountName:
      return firstName([
        identity.playerAccountName,
        identity.playerNickname,
        identity.guildNickname,
        identity.discordName,
      ])
    case PlayerNameMode.GuildNickname:
      return firstName([
        identity.guildNickname,
        identity.playerNickname,
        identity.playerAccountName,
        identity.discordName,
      ])
  }
}

/**
 * Format the same identity a Discord mention resolves to in the client:
 * `@Guild nickname (in-game name)`. The fallback remains useful for legacy
 * unlinked rows that do not have a current Discord guild-member record.
 */
export function playerDisplayName(
  identity: PlayerIdentity,
  mode: PlayerNameModeType = PlayerNameMode.GuildNickname,
): string {
  const nickname = playerNameForMode(identity, mode)
  const inGameName = identity.inGameName.trim()

  if (nickname === '') return inGameName || '—'

  const mention = `@${nickname}`
  if (inGameName === '' || inGameName.localeCompare(nickname, undefined, { sensitivity: 'accent' }) === 0) {
    return mention
  }
  return `${mention} (${inGameName})`
}
